import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { PlatformCommunicationEvents, SessionExpiredEventPayload } from '@shared/generalInterfaces';
import { Router } from '@angular/router';
import { Consts } from '@shared/consts';
import { ShellContextService } from '../shell-context/shell-context.service';
import { PlatformCommunicationService } from 'app/platform/services/platform-communication/platform-communication.service';
import { Location } from '@angular/common';
import { SessionInfo } from './auth.service';
import { AuthAngularService } from '@itero/auth-angular';
import { CookieReadWriteService } from './cookie-read-write.service';
import { PlatformLoginService } from '../platform-login/platform-login.service';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { PlatformLoginStateService } from '../platform-login/platform-login-state.service';

@Injectable({
	providedIn: 'root',
})
export class TokenRefreshService {
	constructor(
		private router: Router,
		private shellContextService: ShellContextService,
		private platformCommunicationService: PlatformCommunicationService,
		private location: Location,
		private authAngularService: AuthAngularService,
		private readonly cookieReadWriteService: CookieReadWriteService,
		private readonly platformLoginService: PlatformLoginService,
		private readonly globalSettingsService: GlobalSettingsService,
		private readonly platformLoginStateService: PlatformLoginStateService
	) {}

	handleAuthenticationRequired() {
		return this.authAngularService.loginRequired$.pipe(
			tap(authInfo => {
				// Skip token-expired handling during login-callback - the login flow manages its own auth.
				const currentUrl = this.router.url;

				if (currentUrl.startsWith('/login-callback') || currentUrl.startsWith('/post-login')) {
					return;
				}

				this.dispatchExpiredTokenEvent(authInfo.sessionId);
			})
		);
	}

	handleRefreshSuccess() {
		return this.authAngularService.refreshSucceed$.pipe(
			tap(authInfo => {
				this.updateSessionInfo({ ...(authInfo as SessionInfo) });

				// Keep the platform login package's token in sync for platform login sessions.
				if (this.platformLoginStateService.isActive()) {
					this.syncPlatformLoginToken(authInfo as SessionInfo);
				}
			})
		);
	}

	dispatchExpiredTokenEvent(sessionId: string) {
		const payload: SessionExpiredEventPayload = { returnUrl: this.getReturnUrl() };

		const sessionExpired = new CustomEvent('midc_event_onTokenExpired', {
			detail: {
				appId: 'midc',
				sessionId,
				payload: payload.returnUrl?.includes('mfe-wrapper') ? {} : payload,
			},
		});

		window.dispatchEvent(sessionExpired);
	}

	private syncPlatformLoginToken(sessionInfo: SessionInfo): void {
		// Use the token email, not loginEmail: logon-as keys the token by the authenticated user.
		const username = this.platformLoginService.getEmailFromAccessToken(sessionInfo.accessToken);

		if (username && sessionInfo.sessionId && sessionInfo.accessToken) {
			this.platformLoginService.updateToken(username, sessionInfo.sessionId, sessionInfo.accessToken);
		}
	}

	private getReturnUrl() {
		const currentUrl = this.location.path();
		const params = this.router.parseUrl(currentUrl).queryParams;

		if (params.returnUrl) {
			return params.returnUrl;
		} else if (currentUrl) {
			return currentUrl;
		}
	}

	private updateSessionInfo(updatedSessionInfo: SessionInfo) {
		updatedSessionInfo.sessionType = Consts.SessionType;
		this.shellContextService.updateContext(context => {
			context.session.sessionId = updatedSessionInfo.sessionId;
			context.security.accessToken = updatedSessionInfo.accessToken;
		});

		const identityProvider = this.cookieReadWriteService.authCookie.cookie.identityProvider;

		this.cookieReadWriteService.clearAuthCookie();
		this.cookieReadWriteService.writeAuthCookie({
			accessToken: updatedSessionInfo.accessToken,
			sessionId: updatedSessionInfo.sessionId,
			idToken: updatedSessionInfo.idToken,
			identityProvider,
		});

		const tokenRefreshedEventPayload = { accessToken: updatedSessionInfo.accessToken };

		this.platformCommunicationService.publishEvent(PlatformCommunicationEvents.TokenRefreshed, ['*'], tokenRefreshedEventPayload);
	}
}
